import React from "react";
import { motion } from "framer-motion";
import { MapPin, DollarSign, CheckCircle2, ArrowRight } from "lucide-react";
import { POSITIONS } from "@/data/positions";

export function PositionsGrid({ onApply }) {
  return (
    <section id="positions" className="py-20 lg:py-28 border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand mb-3">Open Positions</div>
            <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight text-slate-900">
              Roles we're hiring for right now.
            </h2>
          </div>
          <div className="text-sm text-slate-600">
            Don't see your role? Send us your resume anyway using the form below.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
          {POSITIONS.map((p, i) => (
            <motion.div
              key={p.key}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: i * 0.04, duration: 0.5 }}
              data-testid={`position-card-${p.key}`}
              className="group bg-white p-8 flex flex-col"
            >
              <div className="flex items-center justify-between">
                <p.icon className="h-8 w-8 text-primary-brand" strokeWidth={1.6} />
                <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className="font-heading text-xl font-bold mt-5 text-slate-900">{p.title}</h3>
              <div className="mt-1 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">{p.type}</div>
              <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-slate-600">
                <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-primary-brand" />{p.location}</span>
                {p.payRange && (
                  <span className="flex items-center gap-1"><DollarSign className="h-3.5 w-3.5 text-primary-brand" />{p.payRange}</span>
                )}
              </div>
              <p className="text-sm text-slate-600 mt-4 leading-relaxed">{p.summary}</p>
              <ul className="mt-4 space-y-2">
                {p.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-xs text-slate-700">
                    <CheckCircle2 className="h-4 w-4 text-primary-brand mt-0.5 flex-none" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-slate-100">
                <button
                  onClick={() => onApply(p.key)}
                  data-testid={`apply-${p.key}`}
                  className="w-full inline-flex items-center justify-center gap-2 bg-primary-brand hover:bg-[#E04F00] text-white px-5 py-3 font-bold uppercase tracking-wider text-xs"
                >
                  Apply for {p.title.split(" ")[0]} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
